import { Loading } from "app/components";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputText } from "primereact/inputtext";
import { Fragment, useEffect, useRef, useState } from "react";
import { createDocument, getUserDocumentList, updateDocument } from "./my-document.service";
import { getConvertedFileName } from "app/utils/utility.service";
import { Dropdown } from "primereact/dropdown";
import { PHOTOGRAPH_DIMENSION } from "app/utils/Constants";
import { SIGNATURE_DIMENSION } from "app/utils/Constants";
// SECURITY: File name validation for WASA compliance
import { validateAndSanitizeFileName } from "app/utils/inputValidation.service";
// SECURITY: Deny-by-default resource access - validate server response against allowlist
import { validateUploadResource } from "app/utils/uploadResourceConfig";

const MyDocument = ({
    documentModal,
    handleDocumentModal,
    sessionValues,
    rowData,
    onUploadSuccess,
}) => {
    const [documentValues, setDocumentValues] = useState({});
    const [documentList, setDocumentList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(null);

    const fileUploadRef = useRef(null);

    const isSafeStoredDocumentName = (name) => {
        if (!name || typeof name !== "string") return false;
        // Server-generated file names are timestamp-user-randomhash.ext
        // e.g. 1737626282000-123-abcdef1234567890.pdf
        return /^[0-9]+-[a-zA-Z0-9_-]+-[a-f0-9]{16}\.(pdf|png|jpg|jpeg)$/i.test(name);
    };

    const verifyUploadPersisted = async (documentId) => {
        const currentList = await getUserDocumentList(sessionValues?.id);
        if (!Array.isArray(currentList)) return false;
        const updatedDoc = currentList.find((d) => String(d?.id) === String(documentId));
        if (!updatedDoc?.fileName) return false;
        return isSafeStoredDocumentName(updatedDoc.fileName);
    };

    const getDocumentSnapshot = async (documentId) => {
        const currentList = await getUserDocumentList(sessionValues?.id);
        if (!Array.isArray(currentList)) return null;
        const doc = currentList.find((d) => String(d?.id) === String(documentId));
        if (!doc) return null;
        return {
            id: doc?.id,
            fileName: doc?.fileName || "",
            mimeType: doc?.mimeType || "",
            fileSize: doc?.fileSize || "",
            updatedAt: doc?.updatedAt || "",
        };
    };

    const didDocumentChange = (beforeSnapshot, afterSnapshot) => {
        if (!beforeSnapshot || !afterSnapshot) return false;
        return (
            String(beforeSnapshot.fileName || "") !== String(afterSnapshot.fileName || "") ||
            String(beforeSnapshot.mimeType || "") !== String(afterSnapshot.mimeType || "") ||
            String(beforeSnapshot.fileSize || "") !== String(afterSnapshot.fileSize || "") ||
            String(beforeSnapshot.updatedAt || "") !== String(afterSnapshot.updatedAt || "")
        );
    };

    // SECURITY: Deny by default - only allow upload if rowData matches declared allowlist (prevents response manipulation)
    const resourceAccess = (() => {
        if (!rowData) return null;
        return validateUploadResource(rowData);
    })();

    const handleModalInputChange = (e) => {
        const { name, value } = e.target;
        setDocumentValues({ ...documentValues, [name]: value });
    };

    const validateInput = () => {
        if (!rowData?.id) {
            if (!documentValues?.files?.name) {
                return false;
            }
        }
        // if (!values?.dateOfBirthImage?.name) {
        //     return false;
        // }
        // if (!values?.addressProofImage?.name) {
        //     return false;
        // }
        // if (!values?.aadharProof?.name) {
        //     return false;
        // }
        return true;
    };

    const handleDocumentSave = async (e) => {
        e.preventDefault();
        setFormSubmitted(true);
        if (!validateInput()) {
            return false;
        }
        setIsLoading(true);
        let formData = new FormData();

        if (!!documentValues.files) {
            // Get the file's actual MIME type to ensure correct extension
            const fileMimeType = documentValues.files.type;
            
            // Map MIME type to correct file extension (must match backend validation)
            const getExtensionFromMime = (mime) => {
                if (mime === 'image/jpeg') return '.jpg';
                if (mime === 'image/png') return '.png';
                if (mime === 'application/pdf') return '.pdf';
                // Default fallback (shouldn't happen if file type is validated)
                return '.jpg';
            };
            
            const correctExtension = getExtensionFromMime(fileMimeType);
            
            // Get original filename and replace extension with correct one
            let originalFileName = documentValues?.fileName || documentValues?.files?.name || 'file';
            
            // Remove existing extension and add correct one
            const fileNameWithoutExt = originalFileName.replace(/\.[^/.]+$/, '');
            const fileNameWithCorrectExt = fileNameWithoutExt + correctExtension;
            
            // SECURITY: Validate and sanitize file name before upload
            const fileNameResult = validateAndSanitizeFileName(fileNameWithCorrectExt);
            if (!fileNameResult.isValid) {
                alert(fileNameResult.error || "Invalid file name");
                setIsLoading(false);
                return;
            }
            
            const convertedFileName = getConvertedFileName(
                fileNameResult.sanitized || fileNameWithCorrectExt
            );
            
            // Ensure converted filename also has correct extension
            const convertedFileNameWithoutExt = convertedFileName.replace(/\.[^/.]+$/, '');
            const finalFileName = convertedFileNameWithoutExt + correctExtension;
            
            // Validate converted file name as well
            const convertedFileNameResult = validateAndSanitizeFileName(finalFileName);
            if (!convertedFileNameResult.isValid) {
                alert("File name validation failed. Please try again.");
                setIsLoading(false);
                return;
            }
            
            formData.append("fileName", `${convertedFileNameResult.sanitized}`);
            formData.append(
                "file",
                documentValues?.files,
                `${convertedFileNameResult.sanitized}`
            );
        }
        // for (const key of Object.keys(rowData)) {
        //     formData.append(key, `${documentValues[key]}`);
        // }
        if (!!rowData?.id) {
            const beforeSnapshot = await getDocumentSnapshot(rowData?.id);
            const record = await updateDocument(rowData?.id, formData);
            if (record.status === false) {
                setIsLoading(false);
                alert(record?.message);
                handleDocumentModal(false);
            } else {
                // SECURITY: Do not trust upload response body/status directly.
                // Verify upload persisted via a fresh server read before success UI,
                // and confirm the document actually changed (before/after snapshot).
                const persisted = await verifyUploadPersisted(rowData?.id);
                const afterSnapshot = await getDocumentSnapshot(rowData?.id);
                const changed = didDocumentChange(beforeSnapshot, afterSnapshot);
                if (!persisted) {
                    setIsLoading(false);
                    alert("Upload could not be verified on server. Please refresh and check document list.");
                    return;
                }
                if (!changed) {
                    setIsLoading(false);
                    alert("Upload request was not applied on server. Please retry.");
                    return;
                }

                setIsLoading(false);
                alert("File uploaded successfully");
                handleDocumentModal(false); // Don't trigger checkChanges to stay on same page
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            }
        } else {
            formData.append("user_id", sessionValues.id);
            const record = await createDocument(formData);
            if (record.status === false) {
                setIsLoading(false);
                alert(record?.message);
                handleDocumentModal(false);
            } else {
                const createdDocumentId = record?.data?.id || rowData?.id;
                // For create flow, verify the current row now has a persisted server-generated file name.
                const persisted = createdDocumentId
                    ? await verifyUploadPersisted(createdDocumentId)
                    : false;
                if (!persisted) {
                    setIsLoading(false);
                    alert("Upload could not be verified on server. Please refresh and check document list.");
                    return;
                }
                setIsLoading(false);
                alert("Record created successfully");
                handleDocumentModal(false); // Don't trigger checkChanges to stay on same page
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
            }
        }
    };

    const myUploader = (event, name) => {
        // SECURITY: Validate and sanitize file name to prevent path traversal attacks
        const fileNameResult = validateAndSanitizeFileName(event.files[0].name);
        if (!fileNameResult.isValid) {
            alert(fileNameResult.error || "Invalid file name. Please use a valid file name.");
            fileUploadRef?.current.clear();
            return;
        }

        // SECURITY: Use allowlist-derived max size, not server response
        const maxSizeMB = resourceAccess?.maxSizeMB ?? 0;
        const maxSizeBytes = convertMBToBytes(maxSizeMB);
        if (maxSizeBytes <= event.files[0].size) {
            alert(
                "You cannot upload file size more than " + maxSizeMB + "MB"
            );
            fileUploadRef?.current.clear();
            return;
        }
        const docName = rowData?.documentName ?? "";
        if (
            docName.includes("Photograph with white background") ||
            docName.includes("Specimen Signature")
        ) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    if (docName.includes("Photograph with white background")) {
                        // SECURITY: Removed console.log to prevent information disclosure
                        // console.log("img", img.height, img.width);
                        if (
                            img.height !== PHOTOGRAPH_DIMENSION.height &&
                            img.width !== PHOTOGRAPH_DIMENSION.width
                        ) {
                            window.alert(
                                `Photograph dimensions must be of width ${PHOTOGRAPH_DIMENSION.width} and height ${PHOTOGRAPH_DIMENSION.height}`
                            );

                            fileUploadRef?.current.clear();
                            return;
                        }
                        setDocumentValues({
                            ...documentValues,
                            [name]: event.files[0],
                            [`fileName`]: event.files[0].name,
                        });
                    } else if (docName.includes("Specimen Signature")) {
                        if (
                            img.height !== SIGNATURE_DIMENSION.height &&
                            img.width !== SIGNATURE_DIMENSION.width
                        ) {
                            window.alert(
                                `Photograph dimensions must be of width ${SIGNATURE_DIMENSION.width} and height ${SIGNATURE_DIMENSION.height}`
                            );

                            fileUploadRef?.current.clear();
                            return;
                        }
                        setDocumentValues({
                            ...documentValues,
                            [name]: event.files[0],
                            [`fileName`]: event.files[0].name,
                        });
                    } else {
                        setDocumentValues({
                            ...documentValues,
                            [name]: event.files[0],
                            [`fileName`]: event.files[0].name,
                        });
                    }
                    // setImageDimensions({ width: img.width, height: img.height });
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(event.files[0]);
            return;
        } else {
            setDocumentValues({
                ...documentValues,
                [name]: event.files[0],
                [`fileName`]: event.files[0].name,
            });
        }
    };

    const onHide = () => {
        handleDocumentModal(false); // Don't trigger checkChanges when just closing without saving
    };
    const renderFooter = () => {
        return (
            <div>
                <div className="grid">
                    <div className="col-3"></div>
                    <div className="col-7">
                        {/* <Button
                            label="Cancel"
                            className="p-button-raised p-button-lg"
                            onClick={() => onHide()}
                        /> */}
                        <Button
                            label="Save"
                            className="p-button-raised p-button-warning p-button-lg"
                            onClick={handleDocumentSave}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const convertMBToBytes = (mb) => {
        const bytes = mb * (1024 * 1024);
        return bytes;
    };

    const enterRequiredField = () => (
        <div className="p-error text-left">Please enter required field.</div>
    );

    const renderDocumentLabel = (data, maxSizeMB) => {
        const name = data?.documentName ?? "";
        if (name.includes("Photograph with white background")) {
            return (
                <Fragment>
                    <label className="mt-1">
                        {`Upload Document* (${PHOTOGRAPH_DIMENSION?.width}px*${PHOTOGRAPH_DIMENSION?.height}px)`}
                    </label>
                    <label className="mt-1">
                        {`(Max Size: ${maxSizeMB}MB)`}
                    </label>
                </Fragment>
            );
        }
        if (name.includes("Specimen Signature")) {
            return (
                <Fragment>
                    <label className="mt-1">
                        {`Upload Document* (${SIGNATURE_DIMENSION?.width}px*${SIGNATURE_DIMENSION?.height}px)`}
                    </label>
                    <label className="mt-1">
                        {`(Max Size: ${maxSizeMB}MB)`}
                    </label>
                </Fragment>
            );
        }
        return (
            <label className="mt-1">
                Upload Document* (Max {maxSizeMB}MB){" "}
            </label>
        );
    };

    const allowed = resourceAccess?.allowed === true;
    const acceptAttr = resourceAccess?.accept ?? "";
    const maxSizeMB = resourceAccess?.maxSizeMB ?? 0;

    const handleDialogKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            e.stopPropagation();
            if (allowed) {
                handleDocumentSave(e);
            }
        }
    };

    return (
        <Dialog
            header={!!rowData?.id ? "Update Document" : "Add Document"}
            visible={documentModal}
            onHide={() => onHide()}
            breakpoints={{ "960px": "75vw" }}
            style={{ width: "40vw" }}
            footer={allowed ? renderFooter() : null}
            className="AnnualExpenseModal"
            position="top"
            closeOnEscape={false}
            draggable={false}
        >
            <div className="text-right mt-4" onKeyDownCapture={handleDialogKeyDown}>
                {isLoading && <Loading />}

                {!allowed ? (
                    <div className="p-error p-3">
                        {resourceAccess?.error ?? "Access denied. This resource is not allowed for upload."}
                    </div>
                ) : (
                    <>
                        <div className="grid">
                            <div className="col-3">
                                <label className="mt-3">Document Name* </label>
                            </div>
                            <div className="col-5">
                                <div className="field text-left">
                                    <label className="ml-4 mt-3">
                                        <strong>{rowData?.documentName}</strong>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="grid">
                            <div className="col-3">{renderDocumentLabel(rowData, maxSizeMB)}</div>
                            <div className="col-5">
                                <div className="field text-left ml-4">
                                    <FileUpload
                                        ref={fileUploadRef}
                                        name="profileImage"
                                        className="fileUpload"
                                        mode="basic"
                                        customUpload
                                        uploadHandler={(event) =>
                                            myUploader(event, "files")
                                        }
                                        auto
                                        chooseLabel={`Choose ${resourceAccess?.fileType ?? rowData?.fileType} file`}
                                        accept={acceptAttr}
                                        maxFileSize={convertMBToBytes(maxSizeMB)}
                                    />
                                    {formSubmitted &&
                                        !documentValues?.files?.name &&
                                        enterRequiredField()}
                                    <label className="ml-4 mt-2">
                                        <strong>
                                            {!!documentValues?.fileName
                                                ? documentValues?.fileName
                                                : rowData?.fileName}
                                        </strong>{" "}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </Dialog>
    );
};

export default MyDocument;
